import { commands, Extension, getStyleProperty } from '@tiptap/core';

/**
 * Types that carry their own alignment attribute — TextAlign's for text blocks and images, TableKit's
 * `align` for the cells. Keeping text-align in their style attribute as well would leave a
 * stale declaration to fight the toolbar.
 */
const SELF_ALIGNING_TYPES = [
  'heading',
  'paragraph',
  'image',
  'textDiv',
  'tableCell',
  'tableHeader'
];

const STYLED_TYPES = [
  'div',
  'section',
  'span',
  'listItem',
  'bulletList',
  'orderedList',
  'blockquote',
  'codeBlock',
  'table',
  'tableRow'
];

// Declarations are matched by their whole property name, as getStyleProperty does, since a
// prefix would take text-align-last for text-align, or --cara-legacy-background-color (a Legacy
// marker) for the background it records.
const withoutTextAlign = (style: string): string =>
  style
    .split(';')
    .map((s) => s.trim())
    .filter((s) => s && !/^text-align\s*:/i.test(s))
    .join('; ');

const styleAttributes = (dropTextAlign: boolean) => ({
  style: {
    default: null as string | null,
    parseHTML: (element: HTMLElement) => {
      const style = element.getAttribute('style');
      if (!style) return null;
      return (dropTextAlign ? withoutTextAlign(style) : style.trim()) || null;
    },
    renderHTML: (attributes: Record<string, string | null>) => {
      let styleString = attributes.style || '';

      if (styleString && dropTextAlign) {
        styleString = withoutTextAlign(styleString);
      }

      if (attributes.backgroundColor) {
        const bgColorStyle = `background-color: ${attributes.backgroundColor}`;
        styleString = styleString ? `${styleString}; ${bgColorStyle}` : bgColorStyle;
      }

      return styleString ? { style: styleString } : {};
    }
  },
  id: {
    default: null as string | null,
    keepOnSplit: false,
    parseHTML: (element: HTMLElement) => element.getAttribute('id'),
    renderHTML: (attributes: Record<string, string | null>) => ({ id: attributes.id || undefined })
  },
  class: {
    default: null as string | null,
    parseHTML: (element: HTMLElement) => element.getAttribute('class'),
    renderHTML: (attributes: Record<string, string | null>) => ({
      class: attributes.class || undefined
    })
  },
  backgroundColor: {
    default: null as string | null,
    parseHTML: (element: HTMLElement) => getStyleProperty(element, 'background-color'),
    renderHTML: () => ({})
  }
});

/**
 * What the server keeps beside a declaration when a document leaves its Legacy look for a design,
 * so it can be given back: the fallbacks the look printed (brand.MarkLegacyFallbacks), and the
 * document's own colors and font family (brand.AdoptDesignLook). Every other node keeps its whole
 * style attribute, but a bare span becomes a TextStyle that keeps only its color, background and
 * font, so their markers are carried beside them. A font family marker stands in for the family
 * the server set aside, so it is kept whether or not the span declares one.
 */
const LOOK_MARKER =
  /(?:^|;)\s*--cara-(legacy-(?:color|background-color)|own-(?:color|background-color|font-family))\s*:\s*([^;]+)/gi;

const lookMarkerAttributes = {
  lookMarkers: {
    default: null as Record<string, string> | null,
    parseHTML: (element: HTMLElement) => {
      const markers: Record<string, string> = {};
      for (const [, marker, value] of (element.getAttribute('style') ?? '').matchAll(LOOK_MARKER)) {
        markers[marker.toLowerCase()] = value.trim();
      }
      return Object.keys(markers).length ? markers : null;
    },
    renderHTML: (attributes: Record<string, unknown>) => {
      const markers = attributes.lookMarkers as Record<string, string> | null;
      const declared: Record<string, unknown> = {
        color: attributes.color,
        'background-color': attributes.backgroundColor,
        'font-family': true
      };
      const style = Object.entries(markers ?? {})
        .filter(([marker]) => declared[marker.replace(/^(?:legacy|own)-/, '')])
        .map(([marker, value]) => `--cara-${marker}: ${value}`)
        .join('; ');
      return style ? { style } : {};
    }
  }
};

export const StyleAttributeExtension = Extension.create({
  addCommands() {
    return {
      splitBlock:
        (options = {}) =>
        (props) => {
          const splitParent = props.tr.selection.$from.parent;
          const clearSplitID = splitParent.isTextblock && Boolean(splitParent.attrs.id);
          const preserveTextDiv = splitParent.type.name === 'textDiv';
          const splitAtStart = props.tr.selection.$from.parentOffset === 0;
          const split = commands.splitBlock(options)(props);
          if (!split || !props.dispatch || (!clearSplitID && !preserveTextDiv)) return split;

          const { $from } = props.tr.selection;
          const rightPos = $from.before();

          // TipTap turns an empty boundary half into its default paragraph. A styled text div
          // instead stays a text div on both sides, with its identity following the authored
          // content. Mid-text splits copy all attributes before keepOnSplit is consulted.
          if (preserveTextDiv) {
            const attrsWithoutID = { ...splitParent.attrs, id: null };
            if (splitAtStart) {
              const left = props.tr.doc.resolve(rightPos).nodeBefore;
              if (left) {
                props.tr.setNodeMarkup(rightPos - left.nodeSize, splitParent.type, attrsWithoutID);
              }
              props.tr.setNodeMarkup(rightPos, splitParent.type, splitParent.attrs);
            } else {
              props.tr.setNodeMarkup(rightPos, splitParent.type, attrsWithoutID);
            }
            return true;
          }

          if (splitAtStart) {
            const left = props.tr.doc.resolve(rightPos).nodeBefore;
            if (left?.attrs.id) {
              props.tr.setNodeMarkup(rightPos - left.nodeSize, undefined, {
                ...left.attrs,
                id: null
              });
            }
          } else if ($from.parent.attrs.id) {
            props.tr.setNodeMarkup(rightPos, undefined, { ...$from.parent.attrs, id: null });
          }
          return true;
        }
    };
  },

  addGlobalAttributes() {
    return [
      { types: SELF_ALIGNING_TYPES, attributes: styleAttributes(true) },
      // A centered cover carries its alignment in the style attribute and nowhere else, because
      // nothing above manages alignment for a div or a section.
      { types: STYLED_TYPES, attributes: styleAttributes(false) },
      { types: ['textStyle'], attributes: lookMarkerAttributes }
    ];
  }
});
